/**
 * Files on a task: pick many at once, drag and drop, or paste. Pictures and videos show as a gallery (videos play
 * in place), everything else as a list with a download link. Large videos show upload progress. When FusionDocs is
 * on, each file can be filed there and sent for signature (see FusionDocs.tsx).
 */
import { useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import { Download, FileText, Film, Paperclip, Trash2, Upload, X } from "lucide-react";
import { useRef, useState } from "react";
import { del, post } from "../lib/api";
import { FileFusionDocs, FusionDocsSignatures, useFusionDocs, useTaskFusionDocs } from "./FusionDocs";
import { Card } from "./ui";

export interface TaskFile { id: string; title: string; filename: string; contentType: string; size?: number; purpose: string; addedBy?: string | null; addedByName?: string | null }

const human = (n?: number) => (n === undefined ? "" : n > 1e6 ? `${(n / 1e6).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1e3))} KB`);
const url = (id: string) => `/api/documents/${id}/content`;

/** One request: fine for small files. Big ones go up in pieces (uploadInParts) so no request runs past the proxy's limit. */
export function upload(f: File, onProgress: (p: number) => void): Promise<{ id: string }> {
  if (f.size > 16 * 1024 * 1024) return uploadInParts(f, onProgress);
  return new Promise((resolve, reject) => {
    const x = new XMLHttpRequest();
    x.open("POST", "/api/documents");
    x.withCredentials = true;
    x.upload.onprogress = (e) => e.lengthComputable && onProgress(e.loaded / e.total);
    x.onload = () => {
      let body: { id?: string; message?: string } = {};
      try { body = JSON.parse(x.responseText); } catch { /* ignore */ }
      if (x.status >= 200 && x.status < 300 && body.id) resolve({ id: body.id });
      else reject(new Error(body.message ?? `Couldn't upload ${f.name}`));
    };
    x.onerror = () => reject(new Error(`No connection — ${f.name} wasn't uploaded`));
    const fd = new FormData();
    fd.append("file", f);
    x.send(fd);
  });
}

const sendPiece = (uploadId: string, offset: number, blob: Blob, onProgress: (loaded: number) => void) =>
  new Promise<number>((resolve, reject) => {
    const x = new XMLHttpRequest();
    x.open("PUT", `/api/documents/uploads/${uploadId}?offset=${offset}`);
    x.withCredentials = true;
    x.setRequestHeader("content-type", "application/octet-stream");
    x.upload.onprogress = (e) => e.lengthComputable && onProgress(e.loaded);
    x.onload = () => {
      let body: { received?: number; message?: string } = {};
      try { body = JSON.parse(x.responseText); } catch { /* ignore */ }
      if (x.status >= 200 && x.status < 300 && typeof body.received === "number") resolve(body.received);
      else reject(new Error(body.message ?? `Upload failed (${x.status})`));
    };
    x.onerror = () => reject(new Error("No connection"));
    x.send(blob);
  });

async function uploadInParts(f: File, onProgress: (p: number) => void): Promise<{ id: string }> {
  const start = await post<{ uploadId: string; chunkSize: number }>("/api/documents/uploads", { filename: f.name, contentType: f.type || "application/octet-stream", size: f.size });
  let at = 0, tries = 0;
  while (at < f.size) {
    try {
      // the server answers with how much it has, so a retry after a dropped connection carries on from there
      at = await sendPiece(start.uploadId, at, f.slice(at, at + start.chunkSize), (loaded) => onProgress((at + loaded) / f.size));
      tries = 0;
    } catch (e) {
      if (++tries > 4) throw new Error(`${(e as Error).message} — ${f.name} wasn't uploaded`);
      await new Promise((r) => setTimeout(r, 1500 * tries));
    }
  }
  onProgress(1);
  return post<{ id: string }>(`/api/documents/uploads/${start.uploadId}/complete`, {});
}

export function TaskFiles({ itemId, files, canEdit, me }: { itemId: string; files: TaskFile[]; canEdit: boolean; me?: string }) {
  const qc = useQueryClient();
  const input = useRef<HTMLInputElement>(null);
  const [queue, setQueue] = useState<Array<{ name: string; p: number; error?: string }>>([]);
  const [drag, setDrag] = useState(false);
  const [view, setView] = useState<TaskFile | null>(null);
  const media = files.filter((f) => /^(image|video)\//.test(f.contentType));
  const docs = files.filter((f) => !/^(image|video)\//.test(f.contentType));

  const add = async (list: File[]) => {
    if (!list.length) return;
    setQueue(list.map((f) => ({ name: f.name, p: 0 })));
    const ids: string[] = [];
    await Promise.all(list.map(async (f, i) => {
      try {
        ids.push((await upload(f, (p) => setQueue((q) => q.map((x, j) => (j === i ? { ...x, p } : x))))).id);
        setQueue((q) => q.map((x, j) => (j === i ? { ...x, p: 1 } : x)));
      } catch (e) {
        setQueue((q) => q.map((x, j) => (j === i ? { ...x, error: (e as Error).message } : x)));
      }
    }));
    if (ids.length) await post(`/api/work/items/${itemId}/attachments`, { documentIds: ids, purpose: "file" });
    void qc.invalidateQueries({ queryKey: ["item", itemId] });
    setQueue((q) => q.filter((x) => x.error));
  };
  const remove = async (f: TaskFile) => {
    if (!confirm(`Remove ${f.filename} from this task?`)) return;
    await del(`/api/work/items/${itemId}/attachments/${f.id}`);
    void qc.invalidateQueries({ queryKey: ["item", itemId] });
  };
  const canRemove = (f: TaskFile) => canEdit || f.addedBy === me;
  const fdOn = useFusionDocs();
  const fd = useTaskFusionDocs(itemId, fdOn && files.length > 0);
  const fdShown = fdOn && fd.data?.enabled !== false;
  const linkOf = (f: TaskFile) => fd.data?.links.find((l) => l.documentId === f.id);
  const fdNames = new Map((fd.data?.links ?? []).filter((l) => l.fusionDocsId).map((l) => [l.fusionDocsId as string, files.find((f) => f.id === l.documentId)?.filename ?? ""]));
  const fdControl = (f: TaskFile, compact?: boolean) => fdShown && fd.data
    ? <FileFusionDocs itemId={itemId} documentId={f.id} filename={f.filename} link={linkOf(f)} canFile={canRemove(f)} compact={compact} canSign={!!fd.data?.signaturesEnabled} />
    : null;

  return (
    <Card title={`Files${files.length ? ` (${files.length})` : ""}`} icon={<Paperclip size={16} />}
      actions={canEdit && <button className="btn btn-sm" onClick={() => input.current?.click()}><Upload size={14} /> Add files</button>}>
      <div
        onDragOver={(e) => { if (canEdit) { e.preventDefault(); setDrag(true); } }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => { e.preventDefault(); setDrag(false); if (canEdit) void add(Array.from(e.dataTransfer.files)); }}
        onPaste={(e) => { if (canEdit && e.clipboardData.files.length) { e.preventDefault(); void add(Array.from(e.clipboardData.files)); } }}
        tabIndex={canEdit ? 0 : undefined}
        className={clsx("rounded-xl outline-none", drag && "ring-2 ring-brand-400 ring-offset-2")}
      >
        {media.length > 0 && (
          <div className="grid grid-cols-3 gap-2 sm:grid-cols-4 xl:grid-cols-5">
            {media.map((f) => (
              <div key={f.id} className="group relative aspect-square overflow-hidden rounded-lg border border-[var(--line)] bg-black/[0.03]">
                <button className="block h-full w-full" onClick={() => setView(f)} title={f.filename}>
                  {f.contentType.startsWith("image/")
                    ? <img src={url(f.id)} alt={f.filename} loading="lazy" className="h-full w-full object-cover" />
                    : <span className="relative block h-full w-full"><video src={`${url(f.id)}#t=0.5`} preload="metadata" muted playsInline className="h-full w-full object-cover" /><Film size={22} className="absolute bottom-1.5 left-1.5 text-white drop-shadow" /></span>}
                </button>
                {canRemove(f) && <button className="absolute right-1 top-1 hidden rounded-md bg-black/60 p-1 text-white group-hover:block" aria-label="Remove" onClick={() => void remove(f)}><Trash2 size={13} /></button>}
                {fdShown && linkOf(f) && <span className="pointer-events-none absolute bottom-1 right-1 rounded bg-black/60 px-1 text-[0.6rem] font-semibold text-white">FusionDocs</span>}
              </div>
            ))}
          </div>
        )}
        {docs.length > 0 && (
          <ul className={clsx("divide-y divide-[var(--line)]", media.length > 0 && "mt-3")}>
            {docs.map((f) => (
              <li key={f.id} className="group flex flex-wrap items-center gap-x-3 gap-y-1 py-2 text-sm">
                <FileText size={18} className="muted shrink-0" />
                <a href={url(f.id)} target="_blank" rel="noreferrer" className="min-w-0 flex-1 truncate font-medium hover:underline">{f.filename}</a>
                <span className="muted shrink-0 text-xs">{human(f.size)}{f.addedByName ? ` · ${f.addedByName}` : ""}</span>
                <a href={`${url(f.id)}?download=1`} className="btn btn-ghost btn-sm !px-1.5" aria-label="Download"><Download size={14} /></a>
                {canRemove(f) && <button className="btn btn-ghost btn-sm !px-1.5 opacity-0 group-hover:opacity-100" aria-label="Remove" onClick={() => void remove(f)}><Trash2 size={14} /></button>}
                {fdControl(f) && <span className="flex basis-full justify-end sm:basis-auto">{fdControl(f)}</span>}
              </li>
            ))}
          </ul>
        )}
        {fdShown && fd.data && <FusionDocsSignatures signatures={fd.data.signatures} names={fdNames} />}
        {fd.error != null && fdOn && <p className="muted mt-3 text-xs">FusionDocs status couldn't be loaded.</p>}
        {!files.length && !queue.length && (
          canEdit
            ? <button className="muted flex w-full flex-col items-center gap-1 rounded-xl border-2 border-dashed border-[var(--line)] px-4 py-6 text-sm hover:border-brand-300 hover:text-brand-700" onClick={() => input.current?.click()}><Upload size={18} /> Drop pictures, videos or files here, paste, or click to choose<span className="text-xs">Several at once · up to 250 MB each</span></button>
            : <p className="muted text-sm">No files.</p>
        )}
        {queue.length > 0 && (
          <ul className="mt-3 space-y-1.5">
            {queue.map((q, i) => (
              <li key={i} className="text-xs">
                <span className="flex justify-between gap-2"><span className="truncate">{q.name}</span><span className={q.error ? "text-red-700" : "muted"}>{q.error ?? `${Math.round(q.p * 100)}%`}</span></span>
                {!q.error && <span className="mt-0.5 block h-1 overflow-hidden rounded bg-black/[0.06]"><span className="block h-full bg-brand-500 transition-all" style={{ width: `${q.p * 100}%` }} /></span>}
              </li>
            ))}
            {queue.some((q) => q.error) && <button className="muted text-xs underline" onClick={() => setQueue([])}>Dismiss</button>}
          </ul>
        )}
      </div>
      <input ref={input} type="file" multiple hidden onChange={(e) => { void add(Array.from(e.target.files ?? [])); e.target.value = ""; }} />
      {view && (
        <div className="fixed inset-0 z-50 flex flex-col bg-black/90" onClick={() => setView(null)}>
          <div className="flex items-center gap-3 px-4 py-3 text-sm text-white" onClick={(e) => e.stopPropagation()}>
            <span className="min-w-0 flex-1 truncate">{view.filename}</span>
            {fdShown && fd.data && <span className="text-white [&_.btn]:!text-white [&_.chip]:!bg-white/10 [&_.chip]:!text-white">{fdControl(view, true)}</span>}
            <a href={`${url(view.id)}?download=1`} className="rounded-md p-1.5 hover:bg-white/10" aria-label="Download"><Download size={18} /></a>
            <button className="rounded-md p-1.5 hover:bg-white/10" onClick={() => setView(null)} aria-label="Close"><X size={18} /></button>
          </div>
          <div className="flex min-h-0 flex-1 items-center justify-center p-4" onClick={(e) => e.stopPropagation()}>
            {view.contentType.startsWith("image/")
              ? <img src={url(view.id)} alt={view.filename} className="max-h-full max-w-full object-contain" />
              : <video src={url(view.id)} controls autoPlay playsInline className="max-h-full max-w-full" />}
          </div>
          {media.length > 1 && (
            <div className="flex justify-center gap-2 overflow-x-auto px-4 pb-4" onClick={(e) => e.stopPropagation()}>
              {media.map((m) => (
                <button key={m.id} onClick={() => setView(m)} className={clsx("h-14 w-14 shrink-0 overflow-hidden rounded border-2", m.id === view.id ? "border-white" : "border-transparent opacity-60")}>
                  {m.contentType.startsWith("image/") ? <img src={url(m.id)} alt="" className="h-full w-full object-cover" /> : <Film size={20} className="m-auto text-white" />}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </Card>
  );
}
